export interface ChangelogRelease {
  version: string;
  publishedAt: string;
  title: string;
  summary: string;
  changes: string[];
}

export interface ChangelogFeed {
  schemaVersion: 1;
  latestVersion: string;
  releases: ChangelogRelease[];
}

const marketingBaseUrl = (import.meta.env.VITE_MARKETING_URL || "https://disk.lai.ia.br").replace(
  /\/$/,
  "",
);

export const CHANGELOG_FEED_URL = `${marketingBaseUrl}/changelog.json`;
export const CHANGELOG_PAGE_URL = `${marketingBaseUrl}/changelog`;
export const CHANGELOG_LAST_SEEN_KEY = "disk:changelog:last-seen";

function recordOf(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === "object" ? (value as Record<string, unknown>) : null;
}

function parseRelease(value: unknown): ChangelogRelease | null {
  const release = recordOf(value);
  if (!release) return null;
  const changes = Array.isArray(release.changes)
    ? release.changes.filter(
        (change): change is string => typeof change === "string" && Boolean(change.trim()),
      )
    : [];
  if (
    typeof release.version !== "string" ||
    !release.version.trim() ||
    typeof release.publishedAt !== "string" ||
    !/^\d{4}-\d{2}-\d{2}$/.test(release.publishedAt) ||
    typeof release.title !== "string" ||
    !release.title.trim() ||
    typeof release.summary !== "string" ||
    !release.summary.trim() ||
    changes.length === 0
  ) {
    return null;
  }
  return {
    version: release.version,
    publishedAt: release.publishedAt,
    title: release.title,
    summary: release.summary,
    changes,
  };
}

export function parseChangelogFeed(value: unknown): ChangelogFeed | null {
  const feed = recordOf(value);
  if (!feed || feed.schemaVersion !== 1 || typeof feed.latestVersion !== "string") return null;
  if (!Array.isArray(feed.releases)) return null;

  const releases: ChangelogRelease[] = [];
  for (const value of feed.releases) {
    const release = parseRelease(value);
    if (!release) return null;
    releases.push(release);
  }
  if (releases.length === 0 || releases[0]?.version !== feed.latestVersion) return null;
  return { schemaVersion: 1, latestVersion: feed.latestVersion, releases };
}

export async function fetchChangelog(signal?: AbortSignal): Promise<ChangelogFeed> {
  const response = await fetch(CHANGELOG_FEED_URL, {
    signal,
    headers: { Accept: "application/json" },
  });
  if (!response.ok) throw new Error(`Changelog respondeu HTTP ${response.status}`);
  const feed = parseChangelogFeed(await response.json());
  if (!feed) throw new Error("Contrato inválido no feed do changelog");
  return feed;
}
